import { useEffect, useRef, useState } from 'react';
import {
  activePlayer,
  controlsRegion,
  reinforcementBreakdown,
  tradeValue,
  truceActive,
  type Action,
  type LootKind,
  type PlayerView,
} from '@estandarte/engine';
import { crestOf, icons, portraitOf, sigilOf } from '../art';
import { ofType } from '../lib/actions';
import type { LootStep } from '../lib/lootTargets';
import { useGame } from '../store/game';
import { useOnline } from '../store/online';
import { useUi } from '../store/ui';
import { bossName, cardSymbol, guildHex, guildName, regionName, S, territoryName, theme, vocationName } from '../theme';
import { CardBack, LootCard, TerritoryCard } from './Cards';
import { DicePanel } from './Dice';

export function Crest({ color, size = 32 }: { color: string; size?: number }) {
  return <img className="crest" src={crestOf(color)} width={size} height={size} alt={`Brasão de ${guildName(color)}`} />;
}

export function Portrait({ vocation, size = 44 }: { vocation: string | null; size?: number }) {
  if (!vocation) return null;
  return <img className="portrait" src={portraitOf(vocation)} width={size} height={size} alt={vocationName(vocation)} />;
}

export function Banner({ color, size = 1 }: { color: string; size?: number }) {
  return <span className="banner" style={{ background: guildHex(color), width: 14 * size, height: 34 * size }} aria-hidden />;
}

/** Cartas de loot que precisam de alvo no mapa (ou de escolher um jogador). */
const TARGETED: LootKind[] = ['strike', 'barrage', 'blessing', 'ward', 'restore', 'paralyze', 'wall', 'teleport', 'steal'];

function CountPicker({
  min,
  max,
  value,
  onChange,
  label,
}: {
  min: number;
  max: number;
  value: number;
  onChange: (n: number) => void;
  label: string;
}) {
  return (
    <div className="stack" style={{ gap: '0.3rem' }}>
      <div className="row">
        <button className="btn small" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} aria-label="menos">
          −
        </button>
        <span className="big-number" style={{ minWidth: '2.5ch', textAlign: 'center' }} aria-live="polite">
          {value}
        </span>
        <button className="btn small" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} aria-label="mais">
          +
        </button>
        <button className="btn small ghost" onClick={() => onChange(min)}>
          mín
        </button>
        <button className="btn small ghost" onClick={() => onChange(max)}>
          máx
        </button>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
        style={{ width: '100%', accentColor: '#23263A' }}
        disabled={min === max}
      />
    </div>
  );
}

function AmountPicker() {
  const { amount, setAmount } = useUi();
  return (
    <div className="row">
      <span className="hint">Por clique:</span>
      <div className="amounts" role="group" aria-label="Tropas por clique">
        {([1, 3, 5, 'all'] as const).map((a) => (
          <button key={a} className={amount === a ? 'on' : ''} onClick={() => setAmount(a)}>
            {a === 'all' ? 'todas' : a}
          </button>
        ))}
      </div>
    </div>
  );
}

export function PendingSection({ view, legal }: { view: PlayerView; legal: Action[] }) {
  const { dispatch, me } = useGame();
  const startLoot = useUi((s) => s.startLoot);
  const pending = view.pending!;
  const mine = pending.player === me;
  const [count, setCount] = useState(pending.kind === 'conquest' ? pending.suggested : 1);
  useEffect(() => {
    if (pending.kind === 'conquest') setCount(pending.suggested);
  }, [pending]);

  if (pending.kind === 'hidden') {
    return (
      <section>
        <p className="hint" style={{ margin: 0 }}>
          Resolvendo o combate…
        </p>
      </section>
    );
  }
  if (!mine) {
    const who = view.players[pending.player]?.name ?? '';
    const what: Record<string, string> = {
      conquest: 'move tropas para o território conquistado',
      lootChoice: 'escolhe o loot',
      bossRegion: 'escolhe onde o boss surge',
      reaction: 'decide sobre o Amuleto da Sorte',
      lootOverflow: 'joga ou descarta um loot (limite de 3)',
      heal: 'escolhe onde curar',
    };
    return (
      <section>
        <p className="hint" style={{ margin: 0 }}>
          {who} {what[pending.kind]}…
        </p>
      </section>
    );
  }

  switch (pending.kind) {
    case 'conquest':
      return (
        <section>
          <h3>Conquista! Mover para {territoryName(pending.to)}</h3>
          <p className="hint" style={{ margin: '0 0 0.4rem' }}>
            De {territoryName(pending.from)} (mínimo {pending.min}, máximo {pending.max}).
          </p>
          <CountPicker min={pending.min} max={pending.max} value={count} onChange={setCount} label="Tropas para mover" />
          <button className="btn primary block" style={{ marginTop: '0.5rem' }} onClick={() => dispatch({ type: 'moveIn', player: me!, count })}>
            Mover {count}
          </button>
        </section>
      );
    case 'lootChoice':
      return (
        <section>
          <h3>Loot do boss: escolha 1</h3>
          <div className="card-row">
            {ofType(legal, 'chooseLoot').map((a, i) => (
              <LootCard key={i} kind={a.kind} size="md" onClick={() => dispatch(a)} actionLabel="Ficar com esta" />
            ))}
          </div>
        </section>
      );
    case 'bossRegion':
      return (
        <section>
          <h3>Mestre dos bosses</h3>
          <p className="hint" style={{ margin: '0 0 0.4rem' }}>
            Você foi eliminado, mas ainda manda nos bosses. Escolha onde o próximo surge.
          </p>
          <div className="row">
            {ofType(legal, 'chooseBossRegion').map((a) => (
              <button key={a.region} className="btn danger" onClick={() => dispatch(a)}>
                {regionName(a.region)}
              </button>
            ))}
          </div>
        </section>
      );
    case 'lootOverflow': {
      const hand = view.players[me!].loot ?? [];
      const plays = ofType(legal, 'playLoot');
      return (
        <section>
          <h3>Loot demais: jogue ou descarte 1</h3>
          <p className="hint" style={{ margin: '0 0 0.4rem' }}>
            O limite é {view.info.ruleset.maxLoot} cartas de loot na mão.
          </p>
          <div className="card-row">
            {hand.map((k, i) => {
              const usable = plays.filter((a) => a.kind === k);
              return (
                <div key={`${k}-${i}`} className="stack" style={{ gap: '0.25rem', alignItems: 'center' }}>
                  <LootCard kind={k} />
                  <div className="row" style={{ gap: '0.25rem' }}>
                    <button
                      className="btn small"
                      disabled={!usable.length}
                      onClick={() => (TARGETED.includes(k) ? startLoot(k) : dispatch(usable[0]))}
                    >
                      Usar
                    </button>
                    <button className="btn small danger" onClick={() => dispatch({ type: 'discardLoot', player: me!, kind: k })}>
                      Descartar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      );
    }
    case 'reaction':
      return (
        <section>
          <p className="hint" style={{ margin: 0 }}>
            Decida sobre o Amuleto da Sorte.
          </p>
        </section>
      );
    case 'heal':
      return (
        <section>
          <h3>{theme.vocations.heal.name}: cura</h3>
          <p className="hint" style={{ margin: 0 }}>
            Você perdeu tropas neste turno. Clique num território seu para colocar 1 tropa.
          </p>
        </section>
      );
  }
}

function LootModeSection({ view, legal, step }: { view: PlayerView; legal: Action[]; step: LootStep | null }) {
  const { dispatch, me } = useGame();
  const ui = useUi();
  const [count, setCount] = useState(1);
  const kind = ui.loot!;
  useEffect(() => setCount(step?.countMax ?? 1), [step?.countMax]);
  const name = theme.loot[kind].name;
  const victims = kind === 'steal' ? ofType(legal, 'playLoot').filter((a) => a.kind === 'steal') : [];
  return (
    <section>
      <h3>{name}</h3>
      {kind === 'steal' ? (
        <div className="stack">
          <p className="hint" style={{ margin: 0 }}>
            Escolha de quem pegar 1 carta de território (precisa ter território vizinho a um seu).
          </p>
          <div className="row">
            {victims.map((a) => (
              <button
                key={a.victim}
                className="btn"
                onClick={() => {
                  dispatch(a);
                  ui.startLoot(null);
                }}
              >
                <Crest color={view.players[a.victim!].color} size={18} /> {view.players[a.victim!].name} ({view.players[a.victim!].cardCount})
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="hint" style={{ margin: '0 0 0.4rem' }}>
          {step?.hint}
        </p>
      )}
      {kind === 'teleport' && step && step.countMax > 0 && (
        <>
          <CountPicker min={1} max={step.countMax} value={Math.min(Math.max(1, count), step.countMax)} onChange={setCount} label="Tropas" />
          <button
            className="btn primary block"
            style={{ marginTop: '0.5rem' }}
            onClick={() => {
              dispatch({ type: 'playLoot', player: me!, kind: 'teleport', from: ui.picks[0], to: ui.picks[1], count: Math.min(Math.max(1, count), step.countMax) });
              ui.startLoot(null);
            }}
          >
            Teleportar
          </button>
        </>
      )}
      {kind === 'barrage' && step?.confirm && (
        <button
          className="btn primary block"
          onClick={() => {
            dispatch(step.confirm!);
            ui.startLoot(null);
          }}
        >
          Lançar a Great Fireball
        </button>
      )}
      <button className="btn ghost small" style={{ marginTop: '0.4rem' }} onClick={() => ui.startLoot(null)}>
        Cancelar
      </button>
    </section>
  );
}

export function ActionSection({ view, legal, step }: { view: PlayerView; legal: Action[]; step: LootStep | null }) {
  const { dispatch, me } = useGame();
  const ui = useUi();
  const [count, setCount] = useState(1);
  const myTurn = me !== null && activePlayer(view) === me;

  const fortifyMax = (() => {
    if (view.phase === 'fortify' && ui.selected && ui.target) {
      const xs = ofType(legal, 'fortify').filter((a) => a.from === ui.selected && a.to === ui.target);
      return xs.length ? Math.max(...xs.map((a) => a.count)) : 0;
    }
    return 0;
  })();
  const sel = `${ui.selected}-${ui.target}`;
  const lastSel = useRef(sel);
  useEffect(() => {
    if (lastSel.current !== sel) {
      lastSel.current = sel;
      setCount(fortifyMax);
    }
  }, [sel, fortifyMax]);

  if (ui.loot && myTurn) return <LootModeSection view={view} legal={legal} step={step} />;

  if (!myTurn) {
    const active = activePlayer(view);
    if (active === null || active < 0) return null;
    const p = view.players[active];
    return (
      <section>
        <p className="hint" style={{ margin: 0 }}>
          {p.kind === 'bot' ? `${p.name} (bot) está jogando…` : `Aguardando ${p.name}…`}
        </p>
      </section>
    );
  }
  if (view.pending) return null;

  const end = legal.find((a) => a.type === 'endPhase');
  const endLabel = S.nextPhase[view.phase] ?? 'Avançar';
  const places = ofType(legal, 'place');

  if (view.phase === 'setup') {
    const left = view.setup!.remaining[me!];
    const now = Math.min(view.info.ruleset.setupBatch - view.setup!.placedThisGo, left);
    return (
      <section>
        <h3>Posicione suas tropas</h3>
        <p style={{ margin: '0 0 0.4rem' }}>
          Coloque até <strong className="num">{now}</strong> agora. Restam <strong className="num">{left}</strong> no total.
        </p>
        <AmountPicker />
        <p className="hint">Clique nos seus territórios no mapa.</p>
        <button className="btn block" onClick={() => dispatch({ type: 'autoSetup', player: me! })}>
          Distribuir automaticamente
        </button>
      </section>
    );
  }

  if (places.length) {
    const br = reinforcementBreakdown(view, me!);
    const frozen = view.effects.some((e) => e.kind === 'frozen' && view.territories[e.territory!]?.owner === me);
    return (
      <section>
        <h3>Reforços</h3>
        <div className="row" style={{ alignItems: 'baseline' }}>
          <span className="big-number">{view.reinforcements}</span>
          <span className="muted">para posicionar</span>
        </div>
        {view.phase === 'reinforce' && (
          <p className="hint" style={{ margin: '0.25rem 0 0.5rem' }}>
            {br.base} pelos territórios
            {br.regions.map((r) => ` + ${r.bonus} (${regionName(r.region)})`).join('')}
            {view.reinforcements > br.total ? ' + cartas/loot' : ''}
          </p>
        )}
        <AmountPicker />
        <p className="hint" style={{ marginBottom: 0 }}>
          Clique nos seus territórios.{frozen ? ' Territórios congelados não recebem tropas.' : ''}
        </p>
      </section>
    );
  }

  if (view.phase === 'reinforce') {
    const mustTrade = legal.length > 0 && legal.every((a) => a.type === 'trade');
    return (
      <section>
        <h3>Reforços</h3>
        {mustTrade ? (
          <p className="error-box" style={{ margin: 0 }}>
            {S.errors.must_trade}
          </p>
        ) : (
          <button className="btn primary block" disabled={!end} onClick={() => end && dispatch(end)}>
            {endLabel}
          </button>
        )}
      </section>
    );
  }

  if (view.phase === 'attack') {
    const from = ui.selected;
    const to = ui.target;
    const single = ofType(legal, 'attack').find((a) => a.from === from && a.to === to && !a.untilEnd);
    const blitz = ofType(legal, 'attack').find((a) => a.from === from && a.to === to && a.untilEnd);
    const power = to ? ofType(legal, 'power').find((a) => a.target === to) : undefined;
    const harmonyAction = legal.find((a) => a.type === 'harmony');
    const bossAttack = ui.bossTarget ? ofType(legal, 'attackBoss').find((a) => a.from === from) : undefined;
    return (
      <section>
        <h3>Ataque</h3>
        {from && (to || bossAttack) ? (
          <div className="stack">
            <div>
              <strong>{territoryName(from)}</strong> <span className="num">({view.territories[from].troops})</span> →{' '}
              {bossAttack ? (
                <strong style={{ color: '#A23B3B' }}>{bossName(view.boss!.id)}</strong>
              ) : (
                <>
                  <strong>{territoryName(to!)}</strong> <span className="num">({view.territories[to!].troops})</span>
                </>
              )}
            </div>
            <div className="row">
              {single && (
                <button className="btn primary" onClick={() => dispatch(single)}>
                  Atacar uma vez
                </button>
              )}
              {blitz && (
                <button className="btn" onClick={() => dispatch(blitz)}>
                  Atacar até o fim
                </button>
              )}
              {bossAttack && (
                <button className="btn danger primary" onClick={() => dispatch(bossAttack)}>
                  Atacar o boss
                </button>
              )}
            </div>
            {power && (
              <button className="btn danger" onClick={() => dispatch(power)}>
                Usar poder ({vocationName('damage')}): −1 tropa em {territoryName(to!)}
              </button>
            )}
          </div>
        ) : (
          <p className="hint" style={{ margin: 0 }}>
            {truceActive(view)
              ? `Trégua até a rodada ${view.info.ruleset.truceRounds}: nada de atacar outra guilda. Bosses podem ser atacados.`
              : from
                ? 'Agora clique num alvo que pulsa (ou no covil do boss).'
                : 'Clique num território seu com 2+ tropas, depois no alvo.'}
          </p>
        )}
        {harmonyAction && (
          <button className="btn block harmony-btn" style={{ marginTop: '0.6rem' }} onClick={() => dispatch(harmonyAction)}>
            Soltar a Rajada de Harmonia ({view.players[me!].harmony}): +1 no maior dado
            {view.players[me!].harmony >= view.info.ruleset.harmonyMax ? ' e no máximo 1 perda' : ''}
          </button>
        )}
        <button className="btn block" style={{ marginTop: '0.6rem' }} disabled={!end} onClick={() => end && dispatch(end)}>
          {endLabel}
        </button>
      </section>
    );
  }

  if (view.phase === 'fortify') {
    const from = ui.selected;
    const to = ui.target;
    const n = Math.min(Math.max(1, count), fortifyMax);
    return (
      <section>
        <h3>Remanejar</h3>
        {from && to && fortifyMax > 0 ? (
          <div className="stack">
            <div>
              {territoryName(from)} → {territoryName(to)}
            </div>
            <CountPicker min={1} max={fortifyMax} value={n} onChange={setCount} label="Tropas para mover" />
            <button className="btn primary" onClick={() => dispatch({ type: 'fortify', player: me!, from, to, count: n })}>
              Mover {n}
            </button>
          </div>
        ) : (
          <p className="hint" style={{ margin: 0 }}>
            {from ? 'Clique num território vizinho seu.' : 'Clique num território seu para mover tropas (deixa sempre 1).'}
          </p>
        )}
        <button className="btn block" style={{ marginTop: '0.6rem' }} disabled={!end} onClick={() => end && dispatch(end)}>
          {endLabel}
        </button>
      </section>
    );
  }
  return null;
}

export function CardsSection({ view, legal }: { view: PlayerView; legal: Action[] }) {
  const { dispatch, me } = useGame();
  const { cardsPicked, toggleCard, clearCards } = useUi();
  if (me === null) return null;
  const hand = view.players[me].cards ?? [];
  const trades = ofType(legal, 'trade');
  const canTrade = trades.length > 0;
  const pickedTrade = trades.find((t) => cardsPicked.length === 3 && cardsPicked.every((c) => t.cards.includes(c)));
  const value = tradeValue(view.info.ruleset, view.tradeCount);
  return (
    <section>
      <h3>
        Cartas <span className="muted num">({hand.length})</span>
        <span className="spacer" />
        <span className="hint">próxima troca: {value}</span>
      </h3>
      {hand.length === 0 ? (
        <div className="row">
          <CardBack deck="territory" mini />
          <p className="hint" style={{ margin: 0, flex: 1 }}>
            Conquiste um território no turno para ganhar uma carta.
          </p>
        </div>
      ) : (
        <div className="card-row">
          {hand.map((c) => (
            <TerritoryCard
              key={c}
              card={c}
              selected={cardsPicked.includes(c)}
              disabled={!canTrade}
              onClick={() => canTrade && toggleCard(c)}
            />
          ))}
        </div>
      )}
      {canTrade && (
        <div className="row" style={{ marginTop: '0.5rem' }}>
          <button
            className="btn primary small"
            disabled={!pickedTrade}
            onClick={() => {
              if (pickedTrade) dispatch(pickedTrade);
              clearCards();
            }}
          >
            Trocar por {value}
          </button>
          <button
            className="btn small"
            onClick={() => {
              dispatch(trades[0]);
              clearCards();
            }}
          >
            Trocar automaticamente
          </button>
        </div>
      )}
      {canTrade && cardsPicked.length === 3 && !pickedTrade && (
        <p className="hint" style={{ margin: '0.3rem 0 0' }}>
          {S.errors.invalid_set} O curinga vale qualquer símbolo (no máximo 2 por troca).
        </p>
      )}
      {hand.some((c) => cardSymbol(c) === 'wild') && !canTrade && (
        <p className="hint" style={{ margin: '0.3rem 0 0' }}>
          O Mapa Rasgado é curinga: vale qualquer símbolo.
        </p>
      )}
    </section>
  );
}

export function LootSection({ view, legal }: { view: PlayerView; legal: Action[] }) {
  const { dispatch, me } = useGame();
  const startLoot = useUi((s) => s.startLoot);
  if (me === null) return null;
  const hand = view.players[me].loot ?? [];
  if (!hand.length && !view.info.ruleset.addons.bosses) return null;
  const plays = view.pending?.kind === 'lootOverflow' ? [] : ofType(legal, 'playLoot');
  const active = view.effects.filter((e) => e.owner === me && !e.territory && !e.link).map((e) => theme.loot[e.kind as LootKind]?.name);
  return (
    <section>
      <h3>
        Loot <span className="muted num">({hand.length}/{view.info.ruleset.maxLoot})</span>
      </h3>
      {active.length > 0 && <p className="hint" style={{ margin: '0 0 0.4rem' }}>Ativo neste turno: {active.join(', ')}.</p>}
      {hand.length === 0 ? (
        <div className="row">
          <CardBack deck="loot" mini />
          <p className="hint" style={{ margin: 0, flex: 1 }}>
            Último golpe num boss ou primeira guilda a dominar uma região.
          </p>
        </div>
      ) : (
        <div className="card-row">
          {hand.map((k, i) => {
            const usable = plays.filter((a) => a.kind === k);
            const onUse = () => (TARGETED.includes(k) ? startLoot(k) : usable[0] && dispatch(usable[0]));
            return (
              <LootCard
                key={`${k}-${i}`}
                kind={k}
                actionLabel={k === 'luck' ? 'reação' : 'Usar'}
                disabled={k === 'luck' || !usable.length}
                onClick={k === 'luck' ? undefined : onUse}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}

export function VocationSection({ view }: { view: PlayerView }) {
  const { me } = useGame();
  if (me === null || !view.info.ruleset.addons.vocations) return null;
  const voc = view.players[me].vocation;
  if (!voc) return null;
  const def = theme.vocations[voc];
  let status = '';
  if (voc === 'damage') status = view.current === me && view.turn.powerUsed ? 'Já usado neste turno.' : 'Disponível: selecione um alvo de ataque.';
  if (voc === 'defense') status = view.defenseUsed.includes(me) ? 'Já usado neste turno adversário.' : 'Pronto para o próximo combate defensivo.';
  if (voc === 'heal' && view.current === me) status = view.turn.lost > 0 ? 'Vai curar 1 tropa no fim do turno.' : 'Sem perdas neste turno.';
  const harmony = voc === 'harmony' ? view.players[me].harmony : null;
  const flowing = voc === 'harmony' && view.effects.some((e) => e.kind === 'flow' && e.owner === me);
  if (voc === 'harmony')
    status = flowing
      ? 'Rajada ativa neste turno.'
      : (harmony ?? 0) >= view.info.ruleset.harmonyMin
        ? 'Pronto para soltar a Rajada na fase de ataque.'
        : `Faltam ${view.info.ruleset.harmonyMin - (harmony ?? 0)} para a Rajada.`;
  return (
    <section className="vocation">
      <Portrait vocation={voc} size={44} />
      <div>
        <h3>
          {def.name} <span className="muted" style={{ fontFamily: 'var(--font-ui)', fontSize: '0.85rem' }}>({def.role})</span>
        </h3>
        <p className="hint" style={{ margin: 0 }}>
          {def.text}
        </p>
        {harmony !== null && <HarmonyBeads value={harmony} max={view.info.ruleset.harmonyMax} />}
        {status && (
          <p style={{ margin: '0.3rem 0 0', fontSize: '0.88rem' }}>
            <em>{status}</em>
          </p>
        )}
      </div>
    </section>
  );
}

/** Contas de harmonia do Monk (cheias até o valor acumulado). */
export function HarmonyBeads({ value, max, small }: { value: number; max: number; small?: boolean }) {
  return (
    <span className={`harmony-beads${small ? ' small' : ''}`} title={`Harmonia ${value} de ${max}`} aria-label={`Harmonia ${value} de ${max}`}>
      {Array.from({ length: max }, (_, i) => (
        <span key={i} className={i < value ? 'on' : ''} />
      ))}
    </span>
  );
}

const PIPS: Record<number, [number, number][]> = {
  1: [[50, 50]],
  2: [[30, 30], [70, 70]],
  3: [[28, 28], [50, 50], [72, 72]],
  4: [[30, 30], [70, 30], [30, 70], [70, 70]],
  5: [[28, 28], [72, 28], [50, 50], [28, 72], [72, 72]],
  6: [[30, 26], [70, 26], [30, 50], [70, 50], [30, 74], [70, 74]],
};

/** Dado do boss: as faces que fazem o boss surgir (5 e 6) têm os chifres. */
export function BossDie({ value, spawnFaces }: { value: number; spawnFaces: number[] }) {
  const horns = spawnFaces.includes(value);
  return (
    <svg className="boss-die" viewBox="0 0 100 100" width={40} height={40} role="img" aria-label={`Dado do boss: ${value}${horns ? ', boss surge' : ''}`}>
      <rect x="6" y="6" width="88" height="88" rx="16" fill={horns ? '#A23B3B' : '#C9CCD6'} opacity={0.55} />
      <rect x="8" y="8" width="84" height="84" rx="15" fill="none" stroke="#23263A" strokeWidth="3" />
      {horns ? (
        <image href={icons.horns} x="22" y="20" width="56" height="56" />
      ) : (
        PIPS[value]?.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="7.5" fill="#23263A" />)
      )}
    </svg>
  );
}

export function BossSection({ view }: { view: PlayerView }) {
  const lastRoll = useGame((s) => s.lastBossRoll);
  if (!view.info.ruleset.addons.bosses) return null;
  const boss = view.boss;
  const faces = view.info.ruleset.bossSpawnFaces;
  if (!boss) {
    return (
      <section>
        <h3>Boss</h3>
        <div className="row">
          {lastRoll ? <BossDie value={lastRoll} spawnFaces={faces} /> : <CardBack deck="boss" />}
          <p className="hint" style={{ margin: 0, flex: 1 }}>
            Nenhum boss ativo. No início de cada rodada, um {faces.join(' ou ')} no dado do boss faz um surgir num covil.
            {lastRoll ? ` Último dado: ${lastRoll}.` : ''}
          </p>
        </div>
      </section>
    );
  }
  const info = theme.bosses[boss.id];
  return (
    <section>
      <div className="boss-panel" style={{ ['--boss-color' as string]: info?.color ?? '#A23B3B' }}>
        <div className="sigil">
          <span className="stain" aria-hidden />
          <img src={sigilOf(boss.id)} alt="" width={78} height={78} />
        </div>
        <div className="boss-info">
          <div className="name">{bossName(boss.id)}</div>
          <div className="title sc">
            {info?.title} · {regionName(boss.region)}
          </div>
          <div className="hearts" aria-label={`Vida ${boss.hp} de ${boss.maxHp}`}>
            {Array.from({ length: boss.maxHp }, (_, i) => (
              <img key={i} src={icons.heart} className={i < boss.hp ? '' : 'lost'} width={16} height={16} alt="" />
            ))}
            <span className="num">
              {boss.hp}/{boss.maxHp}
            </span>
          </div>
          <div className="timer">
            Raid em <strong className="num">{boss.timer}</strong> rodada(s) · defende com {boss.defenseDice} dados · último golpe leva{' '}
            {boss.lootDraw} loot e fica com 1
          </div>
          <p className="raid">{info?.raidText}</p>
          <BossDamage view={view} />
        </div>
      </div>
    </section>
  );
}

/** Quanto de vida cada jogador já tirou do boss (a cada 2, 1 carta de território). */
function BossDamage({ view }: { view: PlayerView }) {
  const boss = view.boss!;
  const per = view.info.ruleset.bossDamagePerCard;
  const rows = Object.entries(boss.damage)
    .map(([p, d]) => ({ p: Number(p), d }))
    .filter((r) => r.d > 0)
    .sort((a, b) => b.d - a.d);
  return (
    <div className="boss-damage">
      <div className="sc">Dano por guilda</div>
      {rows.length === 0 ? (
        <div className="hint-dark">Ninguém tirou vida ainda. A cada {per} de vida, 1 carta de território.</div>
      ) : (
        rows.map((r) => (
          <div key={r.p} className="row" style={{ gap: '0.35rem' }}>
            <Crest color={view.players[r.p].color} size={16} />
            <span style={{ flex: 1 }}>{view.players[r.p].name}</span>
            <span className="num">{r.d}</span>
          </div>
        ))
      )}
    </div>
  );
}

/** Regiões: bônus, quem domina agora e quem dominou primeiro (bônus de loot). */
export function RegionsSection({ view }: { view: PlayerView }) {
  const regions = view.info.content.map.regions;
  const loot = view.info.ruleset.addons.bosses;
  return (
    <section>
      <h3>Regiões</h3>
      <table className="regions">
        <thead>
          <tr>
            <th>Região</th>
            <th className="n">Bônus</th>
            <th>Domina</th>
            {loot && <th>Primeira</th>}
          </tr>
        </thead>
        <tbody>
          {regions.map((r) => {
            const holder = view.players.find((p) => p.alive && controlsRegion(view, p.id, r.id));
            const first = view.regionFirst[r.id];
            const firstP = first !== undefined && first >= 0 ? view.players[first] : null;
            return (
              <tr key={r.id}>
                <td>
                  <span className="dot" style={{ background: theme.regions[r.id].color }} /> {regionName(r.id)}
                </td>
                <td className="n">+{r.bonus}</td>
                <td>{holder ? <Crest color={holder.color} size={16} /> : <span className="hint">—</span>}</td>
                {loot && (
                  <td title={firstP ? `${firstP.name} dominou primeiro e ganhou 1 loot` : 'Ninguém dominou ainda: o primeiro ganha 1 loot'}>
                    {firstP ? (
                      <span className="row" style={{ gap: '0.25rem', flexWrap: 'nowrap' }}>
                        <Crest color={firstP.color} size={16} />
                        <span className="pl-name" style={{ fontSize: '0.8rem' }}>{firstP.name}</span>
                      </span>
                    ) : (
                      <span className="hint">1 loot livre</span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

export function LogSection() {
  const log = useGame((s) => s.log);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollTo({ top: ref.current.scrollHeight });
  }, [log]);
  return (
    <section>
      <h3>Diário de campanha</h3>
      <div className="log" ref={ref} aria-live="polite">
        {log.map((l) => (
          <div key={l.id} className={l.kind}>
            {l.text}
          </div>
        ))}
      </div>
    </section>
  );
}

export function ControlsSection() {
  const { mode, canUndo, undo, fastBots, setFastBots, leaveGame } = useGame();
  const leaveOnline = useOnline((s) => s.leave);
  const [confirmLeave, setConfirmLeave] = useState(false);
  return (
    <section className="row" style={{ borderBottom: 'none' }}>
      {mode === 'local' && (
        <button className="btn small" onClick={undo} disabled={!canUndo} title="Desfazer a última ação (não vale depois de rolar dados)">
          ↶ Desfazer
        </button>
      )}
      {mode === 'local' && (
        <label className="row" style={{ gap: '0.3rem', fontSize: '0.9rem' }}>
          <input type="checkbox" checked={fastBots} onChange={(e) => setFastBots(e.target.checked)} />
          Acelerar bots
        </label>
      )}
      <span className="spacer" />
      {mode === 'online' && confirmLeave ? (
        <>
          <span className="hint">Sair da sala?</span>
          <button className="btn small danger" onClick={leaveOnline}>
            Sair
          </button>
          <button className="btn small ghost" onClick={() => setConfirmLeave(false)}>
            Ficar
          </button>
        </>
      ) : (
        <button className="btn small ghost" onClick={() => (mode === 'online' ? setConfirmLeave(true) : leaveGame())}>
          Sair
        </button>
      )}
    </section>
  );
}
